import React, { useState, useEffect } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCheckoutViewModel } from '../viewmodels/useCheckoutViewModel';
import { useGetPaymentProvidersQuery } from '../services/api';
import ShippingMethodSelector from './ShippingMethodSelector';

interface PaymentFormProps {
  checkout: ReturnType<typeof useCheckoutViewModel>;
}

type PaymentProviderType = 'stripe' | 'manual' | null;

const PaymentForm: React.FC<PaymentFormProps> = ({ checkout }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState<string>('');
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = useState<PaymentProviderType>(null);
  const [pendingReview, setPendingReview] = useState<boolean>(false);

  const {
    checkoutData,
    cart,
    updateShippingMethod,
    updatePaymentMethod,
    goToNextStep,
    goToPreviousStep,
    errors,
    shippingMethods,
    isLoadingShippingMethods,
  } = checkout;

  // Get available payment providers for the cart's region
  const { data: paymentProvidersData, isLoading: isLoadingProviders } = useGetPaymentProvidersQuery(
    cart?.region_id || '',
    { skip: !cart?.region_id }
  );

  const paymentProviders = (paymentProvidersData?.payment_providers || []).filter(Boolean);

  // Check which providers are available
  const hasStripeProvider = paymentProviders.some(p => p.id.startsWith('pp_stripe'));
  const hasManualProvider = paymentProviders.some(p => p.id === 'pp_system_default');

  // Advance to review step after payment method state update commits.
  useEffect(() => {
    if (pendingReview && checkoutData.payment_method) {
      setPendingReview(false);
      goToNextStep();
    }
  }, [pendingReview, checkoutData.payment_method, goToNextStep]);

  // Auto-select provider if only one is available
  useEffect(() => {
    if (paymentProviders.length > 0 && !selectedProvider) {
      if (hasStripeProvider && stripe) {
        setSelectedProvider('stripe');
      } else if (hasManualProvider) {
        setSelectedProvider('manual');
        // Auto-select manual payment
        updatePaymentMethod({
          id: 'pp_system_default',
          provider_id: 'pp_system_default',
          data: {},
        });
      }
    }
  }, [paymentProviders, hasStripeProvider, hasManualProvider, stripe, selectedProvider, updatePaymentMethod]);

  const handleProviderChange = (provider: PaymentProviderType) => {
    setSelectedProvider(provider);
    if (provider === 'manual') {
      updatePaymentMethod({
        id: 'pp_system_default',
        provider_id: 'pp_system_default',
        data: {},
      });
    } else if (provider === 'stripe') {
      // Clear payment method until form is complete
      setIsComplete(false);
    }
  };

  const handleContinue = async () => {
    if (selectedProvider === 'manual') {
      // Manual payment - no validation needed
      goToNextStep();
      return;
    }

    // Stripe payment validation
    if (!stripe || !elements) {
      setCardError('Stripe has not loaded yet. Please try again.');
      return;
    }

    // Validate + tokenize the payment form (no charge yet)
    const { error } = await elements.submit();
    if (error) {
      setCardError(error.message || 'Invalid payment details');
      return;
    }

    // Create a confirmation token while the PaymentElement is still mounted.
    // This captures the collected payment details into a token that can be used
    // later in confirmPayment() without needing the element to be mounted.
    // Note: createConfirmationToken is available at runtime (Stripe.js is always latest)
    // but the @stripe/stripe-js@2.x types don't include it yet.
    const stripeAny = stripe as unknown as {
      createConfirmationToken: (opts: { elements: typeof elements }) => Promise<{
        error?: { message?: string };
        confirmationToken?: { id: string };
      }>;
    };
    const { error: tokenError, confirmationToken } = await stripeAny.createConfirmationToken({ elements });
    if (tokenError || !confirmationToken) {
      setCardError(tokenError?.message || 'Failed to prepare payment. Please try again.');
      return;
    }

    // Store stripe ref + confirmation token (NOT elements) for completeOrder.
    // The token allows confirmPayment() to work even after PaymentElement unmounts.
    updatePaymentMethod({
      id: 'pp_stripe_stripe',
      provider_id: 'pp_stripe_stripe',
      data: { stripe, confirmationTokenId: confirmationToken.id },
    });

    setPendingReview(true);
  };

  const canContinue = () => {
    if (!checkoutData.shipping_method) return false;
    if (selectedProvider === 'manual') return true;
    if (selectedProvider === 'stripe') {
      return stripe && isComplete;
    }
    return false;
  };

  const getContinueDisabledReason = () => {
    if (!checkoutData.shipping_method) return 'Please select a shipping method';
    if (selectedProvider === 'manual') return 'Continue to review your order';
    if (!stripe) return 'Payment system is loading...';
    if (!isComplete) return 'Please enter valid payment information';
    return 'Continue to review your order';
  };

  return (
    <div className="payment-form" data-testid="payment-form">
      <div className="form-section">
        <h2>Shipping Method</h2>
        {isLoadingShippingMethods ? (
          <div className="loading" data-testid="loading-shipping-methods">Loading shipping methods...</div>
        ) : (
          <ShippingMethodSelector
            shippingMethods={shippingMethods}
            selectedMethod={checkoutData.shipping_method}
            onMethodChange={updateShippingMethod}
            error={errors.shipping_method}
          />
        )}
      </div>

      <div className="form-section">
        <h2>Payment Information</h2>

        {isLoadingProviders ? (
          <div className="loading">Loading payment options...</div>
        ) : (
          <>
            {/* Payment provider selection when multiple options available */}
            {(hasStripeProvider || hasManualProvider) && (
              <div className="payment-provider-selection" data-testid="payment-provider-selection">
                {hasStripeProvider && (
                  <label className={`payment-option ${selectedProvider === 'stripe' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="payment_provider"
                      value="stripe"
                      checked={selectedProvider === 'stripe'}
                      onChange={() => handleProviderChange('stripe')}
                      data-testid="payment-provider-stripe"
                    />
                    <span className="payment-option-label">Pay with Card, Klarna, Swish &amp; more</span>
                  </label>
                )}
                {hasManualProvider && (
                  <label className={`payment-option ${selectedProvider === 'manual' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="payment_provider"
                      value="manual"
                      checked={selectedProvider === 'manual'}
                      onChange={() => handleProviderChange('manual')}
                      data-testid="payment-provider-manual"
                    />
                    <span className="payment-option-label">Pay on Delivery / Manual Payment</span>
                  </label>
                )}
              </div>
            )}

            {/* Stripe PaymentElement — supports Card, Klarna, Swish, etc. */}
            {selectedProvider === 'stripe' && (
              <div className="card-element-container">
                <PaymentElement
                  id="payment-element"
                  options={{ layout: 'tabs' }}
                  onChange={(event) => {
                    setIsComplete(event.complete);
                    if (!event.complete && cardError) {
                      // Keep existing error until form is complete
                    } else if (event.complete) {
                      setCardError('');
                    }
                  }}
                />
                {cardError && <span className="error-text">{cardError}</span>}
              </div>
            )}

            {/* Manual payment info */}
            {selectedProvider === 'manual' && (
              <div className="manual-payment-info" data-testid="manual-payment-info">
                <p>You have selected manual payment. No additional payment information is required.</p>
                <p>You can pay upon delivery or via invoice.</p>
              </div>
            )}

            {errors.payment_method && <span className="error-text">{errors.payment_method}</span>}
          </>
        )}

        <div className="payment-security">
          <div className="security-badge">
            {selectedProvider === 'stripe'
              ? '🔒 Your payment information is secure and encrypted'
              : '📦 Pay securely on delivery'
            }
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={goToPreviousStep}
          className="btn btn-secondary"
        >
          Back to Shipping
        </button>
        <button
          type="button"
          onClick={handleContinue}
          className="btn btn-primary btn-large"
          disabled={!canContinue()}
          title={getContinueDisabledReason()}
          data-testid="continue-to-review"
        >
          Review Order
        </button>
      </div>
    </div>
  );
};

export default PaymentForm;
